import * as XLSX from 'xlsx';
import { ChildPracticeMetadata } from '../../../types';
import { standardDateFormat } from '../../../helpers/date';
import { getChildrenWithMwaste, getChildrenWithPharmas, getChildrenWithAmalgam } from './services';
import { getItemStatus, getProcessedDate } from './utils/helpers';

export const amalgamResultsByPractice = async (practices: ChildPracticeMetadata[], customerNumber: string) => {
  const filteredCustomers = practices.map(customer => customer.customer_number);

  try {
    const children = await getChildrenWithAmalgam(customerNumber);

    const filteredPractices = children?.length ? children.filter(customer => filteredCustomers.includes(customer.customer_number)) : [];

    if (!filteredPractices.length) {
      throw new Error('There are no records to download');
    }

    const wb = XLSX.utils.book_new();

    for (let practice of filteredPractices) {
      if (practice?.amalgam_containers && practice.amalgam_containers.length > 0) {
        const sheetName = `${practice.state.substring(0, 2)}_${practice.name.substring(0, 10)}_${practice.customer_number}`;
        const customerData = [
          { A: practice.name.trim() },
          { A: practice.doctor_name.trim() },
          { A: practice.street.trim() },
          { A: `${practice.city.trim()}, ${practice.state.trim()} ${practice.zip.trim()}` },
          { A: `Customer #: ${practice.customer_number.trim()}` },
        ];

        const amalgamList = practice.amalgam_containers
          .sort((a, b) => b.status - a.status)
          .map(item => ({
            Status: getItemStatus(item),
            'Item Number': item.item_number,
            'Item Description': item?.item?.name ?? 'N/A',
            'Serial Number': item.serial_number,
            'Shipped To Practice Date': standardDateFormat(item.shipped_date),
            'Used Date': standardDateFormat(item.used_date),
            'Processed Date': getProcessedDate(item),
            'Processed Weight': item.destroyed_weight,
          }));

        const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true });
        XLSX.utils.sheet_add_json(ws, amalgamList, { origin: 'A8' });
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    }

    XLSX.writeFile(wb, 'Practice Amalgam Waste.xlsx');
  } catch (err) {
    console.error(err);
    throw new Error('There was a problem downloading your report, please try again later');
  }
};

export const amalgamAllResults = (practices: ChildPracticeMetadata[]) => {
  const customers = structuredClone(practices);

  if (customers.length) {
    const wb = XLSX.utils.book_new();
    const sheetName = 'amalgam_waste_report_card';

    const formattedCustomers = customers.sort((a, b) => a.name.localeCompare(b.name))
      .map(({ name, customer_number, city, state, amalgam_available_containers, amalgam_destroyed_containers, amalgam_last_destroyed_date, amalgam_message_short }) => (
        {
          'Practice Name': name,
          'Customer Number': customer_number,
          Location: `${city.trim()}, ${state}`,
          'In Use': amalgam_available_containers,
          Processed: amalgam_destroyed_containers,
          'Last Processed Date': standardDateFormat(amalgam_last_destroyed_date),
          Message: amalgam_message_short
        }
      ));

    const ws = XLSX.utils.json_to_sheet(formattedCustomers);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `${sheetName}.xlsx`);
  } else {
    throw new Error('There are no records to download');
  }
};

export const medicalResultsByPractice = async (practices: ChildPracticeMetadata[], customerNumber: string) => {
  const filteredCustomers = practices.map(customer => customer.customer_number);

  try {
    const children = await getChildrenWithMwaste(customerNumber);

    const filteredPractices = children?.length ? children.filter(customer => filteredCustomers.includes(customer.customer_number)) : [];

    if (!filteredPractices.length) {
      throw new Error('There are no records to download');
    }

    const wb = XLSX.utils.book_new();

    for (let practice of filteredPractices) {
      if (practice?.mwaste_containers && practice.mwaste_containers.length > 0) {
        const sheetName = `${practice.state.substring(0, 2)}_${practice.name.substring(0, 10)}_${practice.customer_number}`;
        const customerData = [
          { A: practice.name.trim() },
          { A: practice.doctor_name.trim() },
          { A: practice.street.trim() },
          { A: `${practice.city.trim()}, ${practice.state.trim()} ${practice.zip.trim()}` },
          { A: `Customer #: ${practice.customer_number.trim()}` },
        ];

        const medicalList = practice.mwaste_containers
          .sort((a, b) => b.status - a.status)
          .map(item => ({
            Status: getItemStatus(item),
            'Item Number': item.item_number,
            'Item Description': item?.item?.name ?? 'N/A',
            'Serial Number': item.serial_number,
            'Shipped To Practice Date': standardDateFormat(item.shipped_date),
            'Used Date': standardDateFormat(item.used_date),
            'Processed Date': getProcessedDate(item),
            'Processed Weight': item.destroyed_weight,
          }));

        const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true });
        XLSX.utils.sheet_add_json(ws, medicalList, { origin: 'A8' });
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    }

    XLSX.writeFile(wb, 'Practice Medical Waste.xlsx');
  } catch (err) {
    console.error(err);
    throw new Error('There was a problem downloading your report, please try again later');
  }
};

export const medicalAllResults = (practices: ChildPracticeMetadata[]) => {
  const customers = structuredClone(practices);

  if (customers.length) {
    const wb = XLSX.utils.book_new();
    const sheetName = 'medical_waste_report_card';

    const formattedCustomers = customers.sort((a, b) => a.name.localeCompare(b.name))
      .map(({ name, customer_number, city, state, mwaste_available_containers, mwaste_destroyed_containers, mwaste_last_destroyed_date, mwaste_message_short }) => (
        {
          'Practice Name': name,
          'Customer Number': customer_number,
          Location: `${city.trim()}, ${state}`,
          'In Use': mwaste_available_containers,
          Processed: mwaste_destroyed_containers,
          'Last Processed Date': standardDateFormat(mwaste_last_destroyed_date),
          Message: mwaste_message_short,
        }
      ));

    const ws = XLSX.utils.json_to_sheet(formattedCustomers);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `${sheetName}.xlsx`);
  } else {
    throw new Error('There are no records to download');
  }
};

export const pharmaceuticalResultsByPractice = async (practices: ChildPracticeMetadata[], customerNumber: string) => {
  try {
    const entries = practices.map(customer => customer.customer_number)
    const customerNumbers = new Set(entries)

    const children = await getChildrenWithPharmas(customerNumber)

    const filteredChildren = children?.length
      ? children.filter(child => customerNumbers.has(child.customer_number))
      : []

    if (!filteredChildren.length) {
      throw new Error('There are no records to download');
    }

    const childrenWithContainers = filteredChildren.filter(child => {
      return child.pharma_containers.length > 0
    })

    const sheets = childrenWithContainers.map(child => {
      const {
        pharma_containers,
        name: practiceName,
        customer_number,
        doctor_name,
        street,
        city,
        state,
        zip,
      } = child

      const sheetName = `${state.substring(0, 2)}_${practiceName.substring(0, 10)}_${customer_number}`

      const customerData = [
        { A: practiceName },
        { A: doctor_name },
        { A: street },
        { A: `${city}, ${state} ${zip}` },
        { A: `Customer #: ${customer_number}` },
      ];

      const pharmaceuticalList = pharma_containers
      .sort((a, b) => b.status - a.status)
      .map(container => ({
        Status: getItemStatus(container),
        'Item Number': container.item_number,
        'Item Description': container?.item?.name ?? 'N/A',
        'Serial Number': container.serial_number,
        'Shipped To Practice Date': standardDateFormat(container.shipped_date),
        'Used Date': standardDateFormat(container.used_date),
        'Processed Date': getProcessedDate(container),
        'Processed Weight': container.processed_weight,
      }))

      return { sheetName, customerData, pharmaceuticalList }
    })

    const wb = XLSX.utils.book_new();

    sheets.forEach(sheet => {
      const { sheetName, customerData, pharmaceuticalList} = sheet
      const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true })
      
      XLSX.utils.sheet_add_json(ws, pharmaceuticalList, { origin: 'A8' })
      XLSX.utils.book_append_sheet(wb, ws, sheetName)
    })

    XLSX.writeFile(wb, 'Practice Pharma Waste.xlsx');
  } catch (err) {
    console.error(err);
    throw new Error('There was a problem downloading your report, please try again later');
  }
};

export const pharmaceuticalAllResults = (practices: ChildPracticeMetadata[]) => {
  const customers = structuredClone(practices);

  if (customers.length) {
    const wb = XLSX.utils.book_new();
    const sheetName = 'pharmaceutical_waste_report_card';

    const formattedCustomers = customers.sort((a, b) => a.name.localeCompare(b.name))
      .map(({ name, customer_number, city, state, pharma_available_containers, pharma_processed_containers, pharma_last_processed_date, pharma_message_short }) => (
        {
          'Practice Name': name,
          'Customer Number': customer_number,
          Location: `${city.trim()}, ${state}`,
          'In Use': pharma_available_containers,
          Processed: pharma_processed_containers,
          'Last Processed Date': standardDateFormat(pharma_last_processed_date),
          Message: pharma_message_short
        }
      ));

    const ws = XLSX.utils.json_to_sheet(formattedCustomers);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `${sheetName}.xlsx`);
  } else {
    throw new Error('There are no records to download');
  }
};
